import type {CSSProperties} from 'react';
import {useTranslation} from 'react-i18next';
import {MapPin} from 'lucide-react';
import {cn} from '@chekinapp/ui';
import {useArrivalProperty} from 'components/arrival';
import {useBrandingTheme} from 'context/brandingTheme';
import {useReservation} from 'context/reservation';
import {useSummary} from 'context/summary';
import {useStaySummary} from 'hooks/useStaySummary';
import {formatDisplayDate} from 'utils/dates';
import {
  FIELD_START,
  FIELD_STEP,
  SKELETON_OFF_S,
  VALUE_IN_S,
  VALUE_TICK_S,
} from './bookingCardTiming';
import {delayAfter, useLateStart} from './useLateStart';
import styles from './entrance.module.css';

type FieldProps = {
  label: string;
  value?: string;
  index: number;
  startedAt: number;
};

function CardField({label, value, index, startedAt}: FieldProps) {
  const late = useLateStart(Boolean(value), startedAt);
  const d = FIELD_START + index * FIELD_STEP;

  return (
    <div
      className={styles.field}
      style={
        {
          '--d': `${d}s`,
          '--off': delayAfter(d + SKELETON_OFF_S, late),
          '--v': delayAfter(d + VALUE_IN_S, late),
          '--tick': `${VALUE_TICK_S}s`,
        } as CSSProperties
      }
    >
      <small>{label}</small>
      <span className={cn(styles.skeleton, value && styles.done)} />
      {value && <b>{value}</b>}
    </div>
  );
}

type Props = {
  startedAt: number;
};

function EntranceBookingCard({startedAt}: Props) {
  const {t, i18n} = useTranslation();
  const stay = useStaySummary();
  const property = useArrivalProperty();
  const {data: branding} = useBrandingTheme();
  const {isLoading: isReservationLoading} = useReservation();
  const {isLoading: isSummaryLoading} = useSummary();
  const isStayLoading = Boolean(isReservationLoading || isSummaryLoading);

  const name = stay.propertyName || property.name || '';
  const location = [property.city, property.country].filter(Boolean).join(', ');
  const logo = branding?.splash_screen_logo;
  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  };
  const checkIn = formatDisplayDate(stay.checkInDate, i18n.language, dateOptions);
  const checkOut = formatDisplayDate(stay.checkOutDate, i18n.language, dateOptions);

  const fields = [
    {label: t('entrance_booking'), value: stay.bookingRef || undefined},
    {
      label: t('entrance_stay'),
      value: checkIn && checkOut ? `${checkIn} – ${checkOut}` : undefined,
    },
    {
      label: t('guests'),
      value: stay.guests ? t('guests_count', {count: stay.guests}) : undefined,
    },
    {
      label: t('entrance_nights'),
      value: stay.nights ? t('nights_count', {count: stay.nights}) : undefined,
    },
  ].filter(field => isStayLoading || field.value);

  return (
    <div className={styles.content} data-entrance="content">
      <div className={styles.card} data-entrance="card">
        <div className={styles.cardHead}>
          <span className={cn(styles.logo, logo && styles.withImage)} aria-hidden>
            {logo ? <img src={logo} alt="" /> : name.charAt(0)}
          </span>
          <span className="min-w-0">
            <b className={styles.cardName}>{name}</b>
            {location && (
              <small className={styles.cardMeta}>
                <MapPin size={12} aria-hidden />
                {location}
              </small>
            )}
          </span>
        </div>
        <i className={styles.rule} />
        <div className={styles.grid}>
          {fields.map((field, index) => (
            <CardField
              key={field.label}
              label={field.label}
              value={field.value}
              index={index}
              startedAt={startedAt}
            />
          ))}
        </div>
        <p className={styles.opening}>
          {t('entrance_opening_booking')}
          <i />
        </p>
      </div>
    </div>
  );
}

export {EntranceBookingCard};
